import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { formatDate, formatNumber, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, Panel, type BadgeTone } from '../ui/Page';
import { percent, productOverview, type HealthBand } from './api';

/**
 * What tenants actually do (context.md §187).
 *
 * Four questions in order: how many got started (activation), what they use (adoption), whether
 * they stay (cohorts), and which ones need a call this week (the list, worst first). Every rate
 * carries the count it came from, and a cohort month that has not happened yet is a blank, not a
 * zero.
 */

const PERIODS = [3, 6, 12];

export function bandTone(band: HealthBand): BadgeTone {
  switch (band) {
    case 'THRIVING':
      return 'positive';
    case 'HEALTHY':
      return 'info';
    case 'QUIET':
      return 'warning';
    case 'DORMANT':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function ProductPanel(): React.JSX.Element {
  const [months, setMonths] = useState(6);
  const [band, setBand] = useState<HealthBand | 'ALL'>('ALL');

  const overview = useQuery({
    queryKey: ['product-overview', months],
    queryFn: () => productOverview(months),
  });

  if (overview.isLoading) return <Loading />;
  if (overview.error)
    return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;

  const data = overview.data!;
  const list = (band === 'ALL' ? data.list : data.list.filter((row) => row.band === band))
    .slice()
    .sort((a, b) => a.score - b.score);

  return (
    <div className="stack">
      <div className="cell-inline">
        {PERIODS.map((period) => (
          <button
            key={period}
            type="button"
            className={`chip${months === period ? ' chip--active' : ''}`}
            onClick={() => setMonths(period)}
          >
            {t('admin.lastMonths').replace('{n}', String(period))}
          </button>
        ))}
        <span className="muted small">
          {t('admin.product.window').replace('{n}', String(data.windowDays))}
        </span>
      </div>

      <div className="metrics">
        <Figure
          label={t('admin.product.tenants')}
          value={formatNumber(data.tenants)}
          hint={t('admin.product.activeTenants').replace('{n}', formatNumber(data.activeTenants))}
        />
        <Figure
          label={t('admin.product.activated')}
          value={percent(data.activation.share)}
          hint={`${formatNumber(data.activation.activated)} / ${formatNumber(data.tenants)}`}
        />
        {(['THRIVING', 'HEALTHY', 'QUIET', 'DORMANT', 'NEW'] as HealthBand[]).map((key) => (
          <Figure key={key} label={t(`admin.band.${key}`)} value={formatNumber(data.bands[key])} />
        ))}
      </div>

      <div className="home-grid">
        <Panel title={t('admin.product.funnel')}>
          <ul className="funnel">
            {data.activation.funnel.map((step) => (
              <li key={step.milestone} className="funnel__step">
                <span className="funnel__label">{t(`admin.milestone.${step.milestone}`)}</span>
                <span
                  className="funnel__bar"
                  style={{ width: `${Math.round((step.share ?? 0) * 100)}%` }}
                />
                <span className="funnel__value">
                  {formatNumber(step.tenants)}{' '}
                  <span className="muted small">{percent(step.share)}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="muted small">{t('admin.product.funnelHint')}</p>
        </Panel>

        <Panel title={t('admin.product.adoption')}>
          <ul className="funnel">
            {data.adoption.map((row) => (
              <li key={row.area} className="funnel__step">
                <span className="funnel__label">{t(`usage.area.${row.area}`)}</span>
                <span
                  className="funnel__bar"
                  style={{ width: `${Math.round((row.share ?? 0) * 100)}%` }}
                />
                <span className="funnel__value">
                  {formatNumber(row.tenants)}{' '}
                  <span className="muted small">{percent(row.share)}</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="muted small">{t('admin.product.adoptionHint')}</p>
        </Panel>
      </div>

      <Panel title={t('admin.product.cohorts')}>
        {data.cohorts.length === 0 ? (
          <p className="muted">{t('admin.product.noCohorts')}</p>
        ) : (
          <div className="table-scroll">
            <table className="table cohorts">
              <thead>
                <tr>
                  <th>{t('admin.product.cohort')}</th>
                  <th className="numeric">{t('admin.product.size')}</th>
                  {data.cohorts[0]!.retained.map((_, index) => (
                    <th key={index} className="numeric">
                      M{index}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.cohorts.map((row) => (
                  <tr key={row.month}>
                    <td>{row.month}</td>
                    <td className="numeric">{formatNumber(row.size)}</td>
                    {row.retained.map((value, index) => (
                      <td
                        key={index}
                        className="numeric cohorts__cell"
                        style={
                          value === null
                            ? undefined
                            : { background: `rgba(79, 70, 229, ${0.08 + value * 0.5})` }
                        }
                      >
                        {percent(value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">{t('admin.product.cohortsHint')}</p>
      </Panel>

      <Panel title={t('admin.product.list')}>
        <div className="toolbar">
          <span className="cell-inline">
            <button
              type="button"
              className={`chip${band === 'ALL' ? ' chip--active' : ''}`}
              onClick={() => setBand('ALL')}
            >
              {t('admin.product.allBands')}
            </button>
            {(['DORMANT', 'QUIET', 'HEALTHY', 'THRIVING', 'NEW'] as HealthBand[]).map((key) => (
              <button
                key={key}
                type="button"
                className={`chip${band === key ? ' chip--active' : ''}`}
                onClick={() => setBand(key)}
              >
                {t(`admin.band.${key}`)}
              </button>
            ))}
          </span>
        </div>

        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>{t('admin.tenant')}</th>
                <th>{t('admin.product.health')}</th>
                <th className="numeric">{t('admin.product.people')}</th>
                <th className="numeric">{t('admin.product.activeDays')}</th>
                <th className="numeric">{t('admin.product.areas')}</th>
                <th>{t('admin.product.milestones')}</th>
                <th>{t('admin.product.lastSeen')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className="cell-stack">
                      <Link to={`/admin/tenants/${row.id}`} className="strong">
                        {row.name}
                      </Link>
                      <span className="muted small">
                        {row.planKey ?? '—'} · {formatDate(row.createdAt)}
                      </span>
                    </span>
                  </td>
                  <td>
                    <Badge tone={bandTone(row.band)}>{t(`admin.band.${row.band}`)}</Badge>
                    <span className="muted small">{` ${row.score}`}</span>
                  </td>
                  <td className="numeric">
                    {formatNumber(row.activeUsers)}
                    <span className="muted small">{` / ${formatNumber(row.members)}`}</span>
                  </td>
                  <td className="numeric">{formatNumber(row.activeDays)}</td>
                  <td className="numeric">{formatNumber(row.areas)}</td>
                  <td>
                    <span className="cell-inline">
                      {row.milestones.map((milestone) => (
                        <Badge key={milestone} tone="neutral">
                          {t(`admin.milestone.${milestone}`)}
                        </Badge>
                      ))}
                    </span>
                  </td>
                  <td className="muted small">
                    {row.lastEventAt ? formatRelative(row.lastEventAt) : t('admin.product.never')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="metric">
      <p className="metric__label">{label}</p>
      <p className="metric__value">{value}</p>
      {hint && <p className="muted small">{hint}</p>}
    </div>
  );
}
